















import {
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch
} from '@nuxtjs/composition-api'
import { Geolocation } from '~/models/common/types'
import { CENTER_OF_GREECE, GR_CY_BG_BOUNDS } from '~/constants/map'
import { GoogleMapsBoundsLiteral } from '~/models/google/map'
import {
  geolocationsEqual,
  geolocationToGoogleGeolocation
} from '~/utils/geolocation'
import { useGoogleMap } from '~/compositions/google/map/'
import { GoogleMapControlPosition } from '~/constants/google/map'

export default defineComponent({
  props: {
    center: {
      type: Object as PropType<Geolocation>,
      default() {
        return CENTER_OF_GREECE
      }
    },
    zoom: {
      type: Number,
      default: 7
    },
    initialZoom: {
      type: Number,
      default: undefined
    },
    minZoom: {
      type: Number,
      default: 7
    },
    gestureHandling: {
      type: String,
      default: 'cooperative'
    },
    marker: {
      type: Object as PropType<Geolocation>,
      default: null
    },
    draggableMarker: {
      type: Boolean,
      default: false
    },
    markerIcon: {
      type: Object as PropType<any>,
      default: null
    },
    bounds: {
      type: Object as PropType<GoogleMapsBoundsLiteral>,
      default() {
        return GR_CY_BG_BOUNDS
      }
    },
    hideMyCurrentLocationButton: {
      type: Boolean,
      default: true
    },
    markerCircleRadius: {
      type: Number,
      default: null
    },
    radiusCircleOptions: {
      type: Object,
      default() {
        return {}
      }
    },
    heightClass: {
      type: String,
      default: 'tw-h-80'
    },
    hideMarker: {
      type: Boolean,
      default: false
    },
    mapClasses: {
      type: [String, Array],
      default: ''
    }
  },
  setup(props: any, { emit }: any) {
    const {
      center,
      marker,
      zoom,
      initialZoom,
      minZoom,
      gestureHandling,
      draggableMarker,
      markerIcon,
      bounds,
      hideMyCurrentLocationButton,
      markerCircleRadius,
      radiusCircleOptions
    } = toRefs(props)
    const {
      createMap,
      createMarker,
      moveMarker,
      addGpsLocationControlToMap,
      setZoom,
      addZoomChangeListener,
      addCircleAroundMarker,
      addCircle
    } = useGoogleMap()

    const mapTemplateRef = ref<HTMLElement | null>(null)
    const loading = ref<boolean>(true)
    const internalMarker = ref<Geolocation | null>(marker.value)

    onMounted(async () => {
      if (!marker.value) {
        await nextTick()
      }
      await createMap(mapTemplateRef, {
        center: marker.value || center.value,
        zoom: initialZoom.value || zoom.value,
        gestureHandling: gestureHandling.value,
        minZoom: minZoom.value,
        restriction: {
          latLngBounds: bounds.value,
          strictBounds: false
        }
      })
      if (props.hideMarker && markerCircleRadius.value) {
        await addCircle({
          radius: markerCircleRadius.value,
          circleOptions: {
            center: geolocationToGoogleGeolocation(center.value),
            resizable: false
          }
        })
      } else {
        await createMarker(marker.value, {
          draggable: draggableMarker.value,
          icon: markerIcon,
          onMarkerPositionChange
        })
        if (markerCircleRadius.value) {
          await addCircleAroundMarker(
            markerCircleRadius.value,
            radiusCircleOptions.value,
            onCircleCenterChange,
            onCircleRadiusChange
          )
        }
      }

      if (!hideMyCurrentLocationButton.value) {
        addGpsLocationControlToMap(
          GoogleMapControlPosition.TOP_LEFT,
          geolocation => {
            moveMarker(geolocation)
            emit('marker-location-change', geolocation)
          }
        )
      }

      await addZoomChangeListener(onZoomChange)
      loading.value = false
    })

    watch(marker, newMarker => {
      // if the marker changed to nullish value or the marker geolocation is the same as before do not do anything
      if (
        !newMarker ||
        (internalMarker.value &&
          geolocationsEqual(newMarker, internalMarker.value))
      ) {
        return
      }
      internalMarker.value = newMarker
      moveMarker(newMarker)
    })
    watch(zoom, newZoom => setZoom(newZoom))

    function onMarkerPositionChange(location: Geolocation) {
      internalMarker.value = location
      emit('marker-location-change', location)
    }

    function onCircleCenterChange(location: Geolocation) {
      internalMarker.value = location
      emit('marker-location-change', location)
    }

    function onCircleRadiusChange(radius: number) {
      emit('circle-radius-change', radius)
    }

    function onZoomChange(zoom: number) {
      emit('zoom-change', zoom)
    }

    return { mapTemplateRef, loading }
  }
})
